import type {
  AnalysisState,
  BatchCloseReason,
  BatchPhase,
  EffectiveState,
  ExecutionItemState,
  ResultCategory,
  ScheduleRunView,
  VorgangStatus,
  ResolutionKind,
  SourceType,
  Role,
  Provider,
} from '@autopilot/contracts';

export type Tone = 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'brand';

export const VORGANG_STATUS_LABEL: Record<VorgangStatus, string> = {
  NEU: 'Neu',
  KLASSIFIZIERT: 'Klassifiziert',
  IN_BEARBEITUNG: 'In Bearbeitung',
  WARTET_EXTERN: 'Wartet extern',
  WARTET_MELDER: 'Wartet auf Melder',
  GELOEST: 'Gelöst',
  GESCHLOSSEN: 'Geschlossen',
};

export const PHASE_LABEL: Record<BatchPhase, { label: string; tone: Tone }> = {
  DRAFT: { label: 'Entwurf', tone: 'neutral' },
  SELECTING: { label: 'Tickets werden geladen', tone: 'info' },
  PREVIEW_READY: { label: 'Vorschau bereit', tone: 'info' },
  PREHEATING: { label: 'Pre-Heating läuft', tone: 'info' },
  REVIEW_READY: { label: 'Prüfung bereit', tone: 'brand' },
  EXECUTING: { label: 'Ausführung läuft', tone: 'info' },
  COMPLETED: { label: 'Abgeschlossen', tone: 'success' },
  PAUSED: { label: 'Pausiert', tone: 'warning' },
  CANCELLED: { label: 'Abgebrochen', tone: 'neutral' },
  FAILED: { label: 'Fehlgeschlagen', tone: 'error' },
  NEEDS_ATTENTION: { label: 'Klärung erforderlich', tone: 'warning' },
};

export const CLOSE_REASON_LABEL: Record<BatchCloseReason, string> = {
  EMPTY_SELECTION: 'Keine Tickets im Auswahlbestand',
  NO_ELIGIBLE_TICKETS: 'Keine ausführbaren Tickets',
  POLICY_CHANGED: 'Gesellschaftsregel wurde geändert',
  AUTOMATION_DISABLED: 'Automatische Läufe wurden ausgeschaltet',
  BUDGET_EXCEEDED: 'Tokenbudget erreicht',
  TIME_LIMIT: 'Maximale Laufzeit erreicht',
  STOPPED_BY_USER: 'Durch Nutzer gestoppt',
  SELECTION_EXPIRED: 'Auswahl abgelaufen',
  SELECTION_FAILED: 'Auswahl fehlgeschlagen',
  EXECUTION_DISABLED: 'Globaler Not-Stopp aktiv',
  COMPLETED: 'Regulär abgeschlossen',
};

export const ANALYSIS_STATE_LABEL: Record<AnalysisState, string> = {
  PENDING: 'Wartet',
  CONTEXT_LOADING: 'Kontext wird geladen',
  GENERATING: 'Vorschlag wird erstellt',
  EVALUATING: 'Wird geprüft',
  READY: 'Bereit',
  NEEDS_REVIEW: 'Neue Prüfung erforderlich',
  EXCLUDED: 'Ausgeschlossen',
  CONFLICT: 'Konflikt',
  FAILED: 'Fehler',
};

export const EXECUTION_STATE_LABEL: Record<ExecutionItemState, { label: string; tone: Tone }> = {
  QUEUED: { label: 'Wartet auf Übergabe', tone: 'neutral' },
  SUBMITTING: { label: 'Übergeben', tone: 'info' },
  PENDING_DELIVERY: { label: 'Versand ausstehend', tone: 'info' },
  SUCCEEDED: { label: 'Erfolgreich gelöst', tone: 'success' },
  CONFLICT: { label: 'Konflikt', tone: 'warning' },
  FAILED: { label: 'Fehler', tone: 'error' },
  UNKNOWN: { label: 'Versand ungeklärt – Klärung nötig', tone: 'error' },
  SENT_NOT_RESOLVED: { label: 'Versendet, nicht gelöst', tone: 'warning' },
  CANCELLED: { label: 'Nicht mehr begonnen', tone: 'neutral' },
};

export const RESULT_CATEGORY_LABEL: Record<ResultCategory, { label: string; tone: Tone }> = {
  SUCCEEDED: { label: 'Erfolgreich gelöst', tone: 'success' },
  DELIVERY_UNKNOWN: { label: 'Versand ungeklärt', tone: 'error' },
  SENT_NOT_RESOLVED: { label: 'Nach Versand nicht abschließbar', tone: 'warning' },
  FAILED: { label: 'Fehler', tone: 'error' },
  CONFLICT: { label: 'Konflikt', tone: 'warning' },
  NEGATIVE_LIST: { label: 'Negativliste', tone: 'neutral' },
  CONTEXT_INCOMPLETE: { label: 'Kontext unvollständig', tone: 'warning' },
  MANUALLY_EXCLUDED: { label: 'Manuell ausgeschlossen', tone: 'neutral' },
  BELOW_THRESHOLD: { label: 'Unter Schwelle / gesperrt', tone: 'neutral' },
  PENDING: { label: 'Offen', tone: 'info' },
};

export const EFFECTIVE_STATE_LABEL: Record<EffectiveState, { label: string; tone: Tone }> = {
  ENABLED: { label: 'Aktiv', tone: 'success' },
  DISABLED: { label: 'Aus', tone: 'neutral' },
  SYNCING: { label: 'Wird übertragen', tone: 'info' },
  DISABLING: { label: 'Wird abgeschaltet', tone: 'warning' },
  BLOCKED: { label: 'Blockiert', tone: 'error' },
};

export const SCHEDULE_RUN_STATUS_LABEL: Record<ScheduleRunView['status'], { label: string; tone: Tone }> = {
  CLAIMED: { label: 'Wird gestartet', tone: 'info' },
  RUNNING: { label: 'Läuft', tone: 'info' },
  COMPLETED: { label: 'Abgeschlossen', tone: 'success' },
  EMPTY_SELECTION: { label: 'Leer – keine Tickets', tone: 'neutral' },
  NO_ELIGIBLE_TICKETS: { label: 'Keine ausführbaren Tickets', tone: 'neutral' },
  SKIPPED_OVERLAP: { label: 'Übersprungen (Vorlauf aktiv)', tone: 'warning' },
  MISSED: { label: 'Verpasst', tone: 'warning' },
  FAILED: { label: 'Fehlgeschlagen', tone: 'error' },
  CANCELLED: { label: 'Abgebrochen', tone: 'neutral' },
};

export const RESOLUTION_KIND_LABEL: Record<ResolutionKind, string> = {
  FINAL_ANSWER: 'Abschließende Antwort',
  NEEDS_INFORMATION: 'Rückfrage nötig',
  REQUIRES_ACTION: 'Weitere Arbeit nötig',
  NOT_ANSWERABLE: 'Nicht beantwortbar',
};

export const SOURCE_TYPE_LABEL: Record<SourceType, string> = {
  ANFRAGE: 'Anfrage',
  NACHRICHT: 'Nachricht',
  NOTIZ: 'Notiz',
  ANHANG: 'Anhang',
  OBJEKT: 'Liegenschaft',
  EINHEIT: 'Einheit',
  VERTRAG: 'Vertrag',
  MELDER: 'Melder',
  FINANZ: 'Finanzen',
  WISSEN: 'Wissen',
  PRAEZEDENZ: 'Ähnlicher Fall',
  LEITPLANKE: 'Leitplanke',
};

export const ROLE_LABEL: Record<Role, string> = { Viewer: 'Viewer', Operator: 'Operator', Admin: 'Admin' };

export const PROVIDER_LABEL: Record<Provider, string> = {
  OPENAI: 'OpenAI',
  ANTHROPIC: 'Anthropic',
  AZURE_OPENAI: 'Azure OpenAI',
  MOCK: 'Mock (Test)',
};

export const SYNC_STATE_LABEL: Record<'PENDING' | 'SYNCED' | 'FAILED', { label: string; tone: Tone }> = {
  PENDING: { label: 'Wird mit Nidus abgeglichen', tone: 'info' },
  SYNCED: { label: 'Mit Nidus abgeglichen', tone: 'success' },
  FAILED: { label: 'Abgleich fehlgeschlagen', tone: 'error' },
};

export const TRIGGER_LABEL = { MANUAL: 'Manuell', SCHEDULED: 'Automatisch' } as const;

/**
 * Stabile Sperrgrund-Codes (BatchTicketRow.ineligibleReason und EligibilityResponse.blockedByReason),
 * Quelle: packages/server/src/services/ticket-state.ts / domain/eligibility.ts.
 */
export const REASON_LABEL: Record<string, string> = {
  MANUALLY_EXCLUDED: 'Manuell ausgeschlossen',
  NEGATIVE_LIST: 'Negativliste',
  CONTEXT_INCOMPLETE: 'Kontext unvollständig',
  NOT_ANALYZED: 'Noch nicht bewertet',
  NEEDS_REVIEW: 'Neue Prüfung erforderlich',
  EVALUATION_INVALID: 'Keine gültige Bewertung',
  NOT_FINAL_ANSWER: 'Keine abschließende Antwort',
  BLOCKER: 'Fachliche Sperre',
  BELOW_CORRECTNESS: 'Richtigkeit unter Schwelle',
  ABOVE_ERROR_RISK: 'Fehlerrisiko über Schwelle',
  NO_PROFILE: 'Kein erlaubtes Bearbeiterprofil',
  CONTENT_EXPIRED: 'Inhalt abgelaufen',
  DELIVERY_NOT_POSSIBLE: 'Versand nicht möglich (kein Empfänger/gesperrt)',
  DUPLICATE_RECIPIENT: 'Jüngerer Vorgang desselben Empfängers im Batch',
  ALREADY_ANSWERED: 'Bereits beantwortet (Nidus oder Casavi)',
  CONTEXT_TOO_LARGE: 'Kontext zu groß',
  CONFLICT: 'Konflikt',
  ANALYSIS_FAILED: 'Verarbeitung fehlgeschlagen',
  // nur in blockedByReason
  ALREADY_EXECUTED: 'Bereits ausgeführt',
  OPERATION_ACTIVE: 'Ausführung läuft',
  AUTOMATION_HOLD: 'Für Automatik zurückgestellt',
  DELIVERY_HOLD: 'Versand in Klärung',
  MANUAL_CLAIM_ACTIVE: 'In manueller Bearbeitung',
};

export function reasonLabel(code: string | null | undefined): string {
  if (!code) return '';
  return REASON_LABEL[code] ?? 'Sonstiger Sperrgrund';
}
